/*
 *  Quantity Selector Component
 *    Webcomponent version of the quantity selector for use on the PDP.
 *
 *    Usage:
 *      <quantity-selector
 *        class="o-quantity-selector"
 *        min="0" max="100"
 *        quantity="${quantity}"
 *        line="${index+1}"
 *      ></quantity-selector>
 *
 *  Version:
 *    1.0.0 - 2021/07/19
 */

import { updateCart } from '@process-creative/slate-theme-tools';
import {
  html,
  useState,
  component,
  unsafeHTML,
  render,
  useEffect,
} from '../../tools/utilities/component';
import { shopifyAjaxErrorGet } from '../../tools/utilities/error';

export declare interface QuantitySelectorElement extends HTMLElement {
  quantity: number;
  min?: number;
  max?: number;
  disabled?: boolean;
}

component({
  name: 'quantity-selector',
  attributes: ['min', 'max', 'line', 'quantity', 'init', 'hiddenSelector'],
  render: (params) => {
    if (!params.min || !params.max || !params.quantity || !params.line)
      return html`<span>Invalid Data</span>`;

    let quantity = parseInt(params.quantity || '0');

    //States
    const min = parseInt(params.min);
    let max: number = parseInt(params.max);
    const lineindex = parseInt(params.line);
    const hidden = params.hiddenSelector || false;

    const [pending, setPending] = useState(false);

    const addToCartControls = document.querySelector(
      '[data-limit-order-quantity]'
    );
    if (addToCartControls) {
      const maxItems: any =
        addToCartControls.getAttribute('data-maximum-order-quantity') || null;
      if (maxItems) {
        max = maxItems;
      }
    }

    useEffect(() => {
      if (params.init !== 'true') {
        params.init = 'true';
        return;
      }

      const clamped = quantity < min ? min : quantity > max ? max : quantity;
      if (clamped != quantity) {
        params.quantity = `${clamped}`;
        return;
      }

      if (isNaN(lineindex)) return;

      (async () => {
        let curQty = quantity + 0;
        await new Promise((resolve) => setTimeout(resolve, 300));
        if (curQty !== quantity) return;

        setPending(true);

        try {
          await updateCart(lineindex, quantity);
        } catch (e) {
          const err = shopifyAjaxErrorGet(e);
          console.error(e);
        }

        setPending(false);
      })();
    }, [quantity]);

    //Shorthands
    let minusDisabled = quantity <= min || pending;
    let plusDisabled = quantity >= max || pending;
    let inputDisabled = max != null && min >= max;

    if (quantity >= max && max >= 0) quantity = max;

    //Disable inputs for hidden state selectors
    if (hidden) {
      minusDisabled = true;
      plusDisabled = true;
      inputDisabled = true;
    }

    //Events
    const reduceClick = () => (params.quantity = `${quantity - 1}`);
    const addClick = () => (params.quantity = `${quantity + 1}`);

    const eventFieldChange = (e: InputEvent) => {
      const element = e.currentTarget as HTMLInputElement;
      let value = element.value.replace(/\D/g, '');
      if (!value || !value.length) return;
      params.quantity = value;
    };

    //Print
    return html`<div
      class="o-quantity-selector__inner has-js ${hidden ? `is-hidden` : ``}"
    >
      <button
        type="button"
        class="
          o-btn 
          o-quantity-selector__button 
          is-minus
          ${minusDisabled ? 'is-disabled' : ''}
        "
        ?disabled=${minusDisabled}
        @click=${reduceClick}
      >
        ${unsafeHTML(
          '<svg class="o-quantity-selector__icon is-minus" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.333 8h9.333"/></svg>'
        )}
      </button>

      <div class="o-quantity-selector__input">
        <input
          type="number"
          class="o-quantity-selector__input-field o-paragraph
            ${inputDisabled ? 'is-disabled' : ''}"
          .value=${quantity}
          @change=${eventFieldChange}
          data-quantity-selector-field
          max=${max}
        />
      </div>
      <button
        type="button"
        class="
          o-btn 
          o-quantity-selector__button 
          is-plus 
          ${plusDisabled ? 'is-disabled' : ''}
        "
        ?disabled=${plusDisabled}
        @click=${addClick}
      >
        ${unsafeHTML(
          '<svg class="o-quantity-selector__icon is-plus" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 3.333v9.334M3.332 8h9.333"/></svg>'
        )}
      </button>
    </div>`;
  },
});
